'use client';

import Link from '@eliadmin/core/src/components/link';

import type {
  GeneralLayout,
  Menu,
} from '@eliadmin/core/src/api/general/types/general-layout-types';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@eliadmin/core/src/components/ui/accordion';

import { getSafeHref, isRelativeHref } from '@eliadmin/core/src/utils/url-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { MdOutlineEmail, MdOutlinePhone } from 'react-icons/md';
import { SocialIcons } from '@eliadmin/core/src/components/header/social-icons';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

export const FooterAccordions = ({
  footerMenu,
  generalLayout,
}: {
  footerMenu: Menu[];
  generalLayout: GeneralLayout;
}) => {
  const { t } = useTranslations();

  return (
    <>
      <Accordion
        type="single"
        collapsible
        className={cn(
          'flex w-full flex-col items-start justify-start gap-6 border-none sm:hidden',
          'ea-footer__accordion',
        )}
      >
        {footerMenu.map(item => (
          <AccordionItem
            key={item.name}
            value={item.name}
            className={cn('w-full p-0', 'ea-footer__accordion-item')}
          >
            <AccordionTrigger
              value={item.name}
              className={cn(
                'w-full',
                'ea-footer__accordion-trigger',
                'p-0 font-heading text-xl font-medium uppercase text-foreground',
              )}
            >
              {item.name}
            </AccordionTrigger>
            <AccordionContent
              className={cn('w-full', 'flex flex-col items-start justify-start gap-1 pt-4')}
            >
              {item.fields?.map(field => {
                const Comp = field.path
                  ? isRelativeHref(getSafeHref(field.path))
                    ? Link
                    : 'a'
                  : 'span';

                return (
                  <Comp
                    key={field.name}
                    href={field.path ? getSafeHref(field.path) : (undefined as any)}
                    className="text-base font-normal text-foreground"
                  >
                    {field.name}
                  </Comp>
                );
              })}
            </AccordionContent>
          </AccordionItem>
        ))}
        <AccordionItem value={'kontakt'} className={cn('w-full p-0', 'ea-footer__accordion-item')}>
          <AccordionTrigger
            className={cn(
              'w-full',
              'ea-footer__accordion-trigger',
              'p-0 font-heading text-xl font-medium uppercase text-foreground',
            )}
          >
            {t('text186')}
          </AccordionTrigger>
          <AccordionContent
            className={cn('w-full', 'flex flex-col items-start justify-start gap-4 pt-4')}
          >
            <div className="order-3 col-span-1 flex w-full flex-col items-start justify-start gap-6">
              <div className="flex w-full flex-col items-start justify-start gap-1">
                <Link
                  prefetch={false}
                  target="_blank"
                  href={`tel:${generalLayout.data.contact.phone}`}
                  className="flex items-center justify-start gap-3 text-base font-medium text-primary"
                >
                  <MdOutlinePhone size={24} />
                  {generalLayout.data.contact.phone}
                </Link>
                <Link
                  prefetch={false}
                  target="_blank"
                  href={'tel:+421 905 515 568'}
                  className="flex items-center justify-start gap-3 text-base font-medium text-primary"
                >
                  <MdOutlinePhone size={24} />
                  +421 905 515 568
                </Link>
                <Link
                  prefetch={false}
                  target="_blank"
                  href={`mailto:${generalLayout.data.contact.email}`}
                  className="flex items-center justify-start gap-3 text-base font-medium text-primary"
                >
                  <MdOutlineEmail size={24} />
                  {generalLayout.data.contact.email}
                </Link>
              </div>
              <div className="items-starts flex w-full flex-col justify-start gap-3">
                <h5 className="font-heading text-xl font-medium uppercase text-foreground">
                  {t('text492')}
                </h5>
                <SocialIcons />
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </>
  );
};
